/**
 * One option group, fully editable: a header (label, type, pick-one/many) with inline
 * Edit/Delete (delete = archive), the list of editable ChoiceRows, the add-choice form,
 * and the per-group assignment block. The header shows plain-language type labels
 * (View options / Prompt options) instead of the raw `axis`/`modifier` storage words.
 */
import { useState } from "react";
import { useServices } from "../app/context.js";
import type { OptionChoice, OptionGroup, OptionGroupKind, OptionSelect } from "@genz/platform";
import { ChoiceRow } from "./ChoiceRow.js";
import { AddChoiceForm } from "./AddChoiceForm.js";
import { GroupAssignment } from "./GroupAssignment.js";
import { KIND_LABELS, KIND_HINTS, SELECT_LABELS } from "./labels.js";
import { RowAction } from "../ui/RowAction.js";

export function GroupCard({
  group,
  onGroupChanged,
  onChoiceCreated,
  onError,
}: {
  group: OptionGroup;
  onGroupChanged: (updated: OptionGroup) => void;
  onChoiceCreated: (c: OptionChoice) => void;
  onError: (msg: string) => void;
}) {
  const { client } = useServices();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(group.label);
  const [kind, setKind] = useState<OptionGroupKind>(group.kind);
  const [select, setSelect] = useState<OptionSelect>(group.select);
  const [busy, setBusy] = useState(false);
  const tid = `option-group-${group.id}`;
  const fail = (e: unknown) => onError(e instanceof Error ? e.message : String(e));

  async function save() {
    setBusy(true);
    try {
      const updated = await client.adminUpdateOptionGroup(group.id, {
        label: label.trim() || group.key,
        kind,
        select,
      });
      onGroupChanged(updated);
      setEditing(false);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      onGroupChanged(await client.adminUpdateOptionGroup(group.id, { archived: true }));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card options-group" data-testid={tid}>
      <div className="options-group__head">
        {editing ? (
          <>
            <input className="input" type="text" value={label} data-testid={`${tid}-edit-label`}
              onChange={(e) => setLabel(e.target.value)} />
            <select className="input" value={kind} data-testid={`${tid}-edit-kind`}
              onChange={(e) => setKind(e.target.value as OptionGroupKind)}>
              <option value="axis">{KIND_LABELS.axis}</option>
              <option value="modifier">{KIND_LABELS.modifier}</option>
            </select>
            <select className="input" value={select} data-testid={`${tid}-edit-select`}
              onChange={(e) => setSelect(e.target.value as OptionSelect)}>
              <option value="single">{SELECT_LABELS.single}</option>
              <option value="multi">{SELECT_LABELS.multi}</option>
            </select>
            {/* WS-Z5 rule 7: a sub-form action inside an expanded group, not the primary. */}
            <button type="button" className="btn btn--sm" disabled={busy}
              data-testid={`${tid}-edit-save`} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
            <button type="button" className="btn btn--sm btn--ghost" data-testid={`${tid}-edit-cancel`}
              onClick={() => { setEditing(false); setLabel(group.label); setKind(group.kind); setSelect(group.select); }}>Cancel</button>
          </>
        ) : (
          <>
            <h3>{group.label}</h3>
            <span className="badge badge--neutral options-group__key" data-testid={`${tid}-key`}>{group.key}</span>
            <span
              className={`badge ${group.kind === "axis" ? "badge--info" : "badge--neutral"}`}
              title={KIND_HINTS[group.kind]}
            >
              {KIND_LABELS[group.kind]}
            </span>
            <span className="badge badge--neutral">{SELECT_LABELS[group.select]}</span>
            {/* WS-Z9: a group is a ROW of the options index, and its two verbs are the same
                two the choice rows under it carry — so they take the same two icons. */}
            <RowAction label="Edit" icon="editor" tone="quiet" data-testid={`${tid}-edit`}
              onClick={() => setEditing(true)} />
            <RowAction label="Delete" icon="trash" tone="danger" data-testid={`${tid}-delete`}
              disabled={busy} onClick={() => void remove()} />
          </>
        )}
      </div>

      <div className="policy-form">
        {group.choices.length === 0 ? (
          <p className="faint" data-testid={`${tid}-no-choices`}>No choices yet.</p>
        ) : group.choices.map((c) => (
          <ChoiceRow key={c.id} groupId={group.id} choice={c}
            onChanged={onChoiceCreated} onError={onError} />
        ))}
        <AddChoiceForm group={group} onCreated={onChoiceCreated} />
      </div>

      <GroupAssignment group={group} />
    </div>
  );
}
